/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Stack,
    Tooltip,
    CircularProgress,
    Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { primary, error, grey } from 'src/theme/palette';
import { collectLeadMediaIdsFromChecklist, mergeAndDedupeAttachments } from './taskAttachmentsUtils';

export { collectLeadMediaIdsFromChecklist, mergeAndDedupeAttachments };

// -----------------------------------------------

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';

function humanSize(bytes) {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentRow({ attachment, onDelete }) {
    const isFromCell = attachment._fromTaskCell === true;
    const filename = attachment.original_filename || attachment.filename || attachment.file_name || attachment.name || attachment.description || 'File';
    const fileUrl = attachment.file || attachment.url || attachment.file_url || attachment.media || attachment.remote_url || null;
    const uploadedBy = attachment.uploaded_by || attachment.created_by;
    const uploaderName = uploadedBy
        ? `${uploadedBy.first_name || ''} ${uploadedBy.last_name || ''}`.trim() || uploadedBy.email || '—'
        : '—';
    const uploadedAt = attachment.uploaded_at || attachment.created_at;
    const size = humanSize(attachment.file_size || attachment.size || 0);

    const formattedDate = uploadedAt
        ? new Date(uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

    return (
        <Box
            className="task-slideout-attachment-row"
            data-testid="task-slideout-attachment-row"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                py: '6px',
                px: '8px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': { borderColor: 'primary.light', bgcolor: alpha(primary.main, 0.02) },
            }}
        >
            <Iconify icon="eva:attach-2-fill" width={16} sx={{ color: grey[400], flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                    <Typography
                        className="task-slideout-attachment-filename"
                        data-testid="task-slideout-attachment-filename"
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {filename}
                    </Typography>
                    {isFromCell && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: 'primary.dark',
                                bgcolor: alpha(primary.main, 0.1),
                                px: 0.6,
                                py: 0.1,
                                borderRadius: 0.5,
                                lineHeight: 1.3,
                            }}
                        >
                            From task cell
                        </Box>
                    )}
                </Stack>
                <Typography className="task-slideout-attachment-meta" data-testid="task-slideout-attachment-meta" variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', fontWeight: 400 }}>
                    {size} &middot; {uploaderName} &middot; {formattedDate}
                </Typography>
            </Box>

            <Box className="task-slideout-attachment-actions" data-testid="task-slideout-attachment-actions" sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                {fileUrl && (
                    <Tooltip title="Download">
                        <IconButton
                            size="small"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                            sx={{ color: primary.main }}
                        >
                            <Iconify icon="eva:download-outline" width={18} />
                        </IconButton>
                    </Tooltip>
                )}
                {!isFromCell && (
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => onDelete(attachment.id)}
                            sx={{ color: error.main, '&:hover': { bgcolor: alpha(error.main, 0.08) } }}
                        >
                            <Iconify icon="eva:trash-2-outline" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

AttachmentRow.propTypes = {
    attachment: PropTypes.shape({
        id: PropTypes.any,
        original_filename: PropTypes.string,
        filename: PropTypes.string,
        file_name: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        file: PropTypes.string,
        url: PropTypes.string,
        file_url: PropTypes.string,
        media: PropTypes.string,
        remote_url: PropTypes.string,
        file_size: PropTypes.number,
        size: PropTypes.number,
        uploaded_by: PropTypes.object,
        created_by: PropTypes.object,
        uploaded_at: PropTypes.string,
        created_at: PropTypes.string,
        _fromTaskCell: PropTypes.bool,
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
};

// -----------------------------------------------

export default function TaskAttachments({ practiceId, taskId, leadId, checklist, open: sectionOpen }) {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const fileInputRef = useRef(null);

    const fetchAttachments = async () => {
        if (!practiceId || !taskId) return;
        setLoading(true);
        try {
            const taskRes = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/attachments/`);
            const taskList = taskRes?.status === 200
                ? (taskRes.data?.results || taskRes.data || [])
                : [];
            if (taskRes?.status !== 200) {
                console.warn('[TaskAttachments] Unexpected response:', taskRes?.status, taskRes?.data);
            }

            // Pull any LeadMedia rows uploaded via UPLOAD_DOCUMENT cells on
            // this task. Only fetch if we have a leadId AND the checklist has
            // upload-document cells with a lead_media_id — otherwise skip the
            // network round-trip.
            let leadMediaList = [];
            const leadMediaIds = collectLeadMediaIdsFromChecklist(checklist);
            if (leadId && leadMediaIds.length > 0) {
                const idsParam = leadMediaIds.map((v) => encodeURIComponent(v)).join(',');
                const lmRes = await callApi(
                    `/api/v1/practices/${practiceId}/leads-media/?lead__in=${leadId}&id__in=${idsParam}&limit=100`
                );
                if (lmRes?.status === 200) {
                    leadMediaList = lmRes.data?.results || lmRes.data || [];
                } else if (lmRes?.status) {
                    // Fallback path: id__in may not be supported. Pull by lead
                    // and filter client-side. The set is small (slideout view).
                    const lmAll = await callApi(
                        `/api/v1/practices/${practiceId}/leads-media/?lead__in=${leadId}&limit=100&ordering=-created_at`
                    );
                    if (lmAll?.status === 200) {
                        const idSet = new Set(leadMediaIds.map(String));
                        leadMediaList = (lmAll.data?.results || lmAll.data || []).filter((r) => idSet.has(String(r.id)));
                    }
                }
            }

            setAttachments(mergeAndDedupeAttachments(taskList, leadMediaList));
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_attachments_fetch' } });
            dispatch(updateError({ success: false, message: 'Failed to load attachments.' }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (sectionOpen && practiceId && taskId) {
            fetchAttachments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionOpen, practiceId, taskId, leadId, JSON.stringify(collectLeadMediaIdsFromChecklist(checklist))]);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset the input so same file can be re-selected
        e.target.value = '';

        if (file.size > MAX_FILE_BYTES) {
            dispatch(updateError({ success: false, message: 'File exceeds 10MB limit. Please choose a smaller file.' }));
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/attachments/upload/`,
                'post',
                formData
            );

            if (res.status === 200 || res.status === 201) {
                fetchAttachments();
            } else {
                console.warn('[TaskAttachments] Upload failed:', res.status, res.data);
                const msg = res.data?.detail || res.data?.file?.[0] || 'Upload failed. Please try again.';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_attachment_upload' } });
            dispatch(updateError({ success: false, message: 'Upload failed. Please try again.' }));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (attachmentId) => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/attachments/${attachmentId}/`,
                'delete'
            );
            if (res.status === 204 || res.status === 200) {
                setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete attachment.' }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_attachment_delete' } });
            dispatch(updateError({ success: false, message: 'Failed to delete attachment.' }));
        }
    };

    return (
        <Box>
            {/* Section header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={
                        <Iconify
                            icon={expanded ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'}
                            width={14}
                        />
                    }
                    onClick={() => setExpanded(!expanded)}
                    sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.7rem', color: 'text.secondary', px: 0 }}
                >
                    Attachments{attachments.length > 0 ? ` (${attachments.length})` : ''}
                </Button>
                <Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="eva:upload-fill" width={14} />}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                </Box>
            </Stack>

            <Collapse in={expanded}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {!loading && attachments.length === 0 && (
                    <Box
                        sx={{
                            py: 2.5,
                            textAlign: 'center',
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                        }}
                    >
                        <Typography variant="caption" color="text.disabled">
                            No attachments yet. Click &ldquo;Upload&rdquo; to add files.
                        </Typography>
                    </Box>
                )}
                {!loading && attachments.length > 0 && (
                    <Stack spacing={0.75}>
                        {attachments.map((a) => (
                            <AttachmentRow key={a.id} attachment={a} onDelete={handleDelete} />
                        ))}
                    </Stack>
                )}
            </Collapse>
        </Box>
    );
}

TaskAttachments.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checklist: PropTypes.array,
    open: PropTypes.bool,
};

TaskAttachments.defaultProps = {
    leadId: null,
    checklist: [],
    open: true,
};
